// src/pages/admins/milestoneViewUtils.js
//
// Display-only rules for the Milestones tab. Nothing here changes what a
// milestone IS -- completion is still owned by the backend's
// deriveMilestoneStatus() (services/projectWorkspaceService.js): a milestone
// is completed only once it has an actual_date, recorded through Mark
// Complete or the Edit modal. Task completion is a separate signal:
// - 100% of tasks done but no actual_date yet -> "Ready to Complete", never
//   "Completed" (no artificial actual_date) and never "Overdue" (the work is
//   done; only the milestone's sign-off is outstanding).
// - Lateness of a completed milestone is the backend's delay_days (actual vs
//   planned), never today's date.
import dayjs from "dayjs";

const isCompletedStatus = (status) => status === "COMPLETED_ON_TIME" || status === "COMPLETED_LATE";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function allTasksCompleted(summary) {
  return !!summary && summary.total > 0 && summary.progress_percent === 100;
}

function hasTaskActivity(summary) {
  return !!summary && ((summary.completed || 0) > 0 || (summary.in_progress || 0) > 0);
}

// MySQL TINYINT arrives as 0/1, not a boolean.
export function isMilestoneInactive(m) {
  return m.is_active === false || m.is_active === 0;
}

export function isPhaseInactive(m) {
  return m.phase_is_active === false || m.phase_is_active === 0;
}

// Active = active milestone on an active (or no) phase -- the same rule as
// the backend's ACTIVE_MILESTONE_WHERE default view.
export function isActivePlanMilestone(m) {
  return !isMilestoneInactive(m) && !isPhaseInactive(m);
}

// Badge shown on a milestone row: { key, label, tone, detail, title }.
export function milestoneDisplayStatus(m) {
  const summary = m.task_summary;
  if (m.status === "COMPLETED_ON_TIME") {
    return { key: "COMPLETED", label: "Completed", tone: "success", detail: null };
  }
  if (m.status === "COMPLETED_LATE") {
    const days = Number(m.delay_days) || 0;
    return { key: "COMPLETED_LATE", label: "Completed Late", tone: "warning", detail: days > 0 ? `${plural(days, "day")} late` : null };
  }
  if (allTasksCompleted(summary)) {
    const overdue = m.status === "OVERDUE" ? Number(m.overdue_days) || 0 : 0;
    return {
      key: "READY",
      label: "Ready to Complete",
      tone: "info",
      detail: "Awaiting confirmation",
      title: overdue > 0
        ? `All tasks are completed. Planned date passed ${plural(overdue, "day")} ago -- mark the milestone complete to record its actual date.`
        : "All tasks are completed -- mark the milestone complete to record its actual date.",
    };
  }
  if (m.status === "OVERDUE") {
    const days = Number(m.overdue_days) || 0;
    return { key: "OVERDUE", label: "Overdue", tone: "danger", detail: days > 0 ? `${plural(days, "day")} overdue` : null };
  }
  if (m.status === "DUE_TODAY") {
    return { key: "DUE_TODAY", label: "Due Today", tone: "warning", detail: null };
  }
  if (m.status === "UPCOMING" && hasTaskActivity(summary)) {
    return { key: "IN_PROGRESS", label: "In Progress", tone: "primary", detail: null };
  }
  if (m.status === "UPCOMING") {
    return { key: "UPCOMING", label: "Upcoming", tone: "secondary", detail: null };
  }
  return { key: m.status || "UNKNOWN", label: m.status_label || (m.status ? String(m.status).replace(/_/g, " ") : "Unknown"), tone: "secondary", detail: null };
}

// The summary-strip bucket each milestone counts toward. The four buckets
// partition the milestones, so they always add up to the total:
// completed (has actual_date) | overdue (past plan, tasks not all done) |
// in_progress (work started, incl. Ready to Complete) | upcoming (no work yet).
export function milestoneCategory(m) {
  if (isCompletedStatus(m.status)) return "completed";
  const display = milestoneDisplayStatus(m);
  if (display.key === "OVERDUE") return "overdue";
  if (display.key === "READY" || hasTaskActivity(m.task_summary)) return "in_progress";
  return "upcoming";
}

export const MILESTONE_CATEGORIES = [
  { key: "completed", label: "Completed", icon: "fa-circle-check" },
  { key: "in_progress", label: "In Progress", icon: "fa-bars-progress" },
  { key: "upcoming", label: "Upcoming", icon: "fa-calendar-days" },
  { key: "overdue", label: "Overdue", icon: "fa-triangle-exclamation" },
];

export function summarizeMilestones(milestones) {
  const counts = { total: milestones.length, completed: 0, in_progress: 0, upcoming: 0, overdue: 0, ready: 0 };
  milestones.forEach((m) => {
    counts[milestoneCategory(m)] += 1;
    if (milestoneDisplayStatus(m).key === "READY") counts.ready += 1;
  });
  return counts;
}

// Status filter values: a summary category, or "ready" (a subset of
// in_progress) so sign-off candidates can be found directly.
export function matchesMilestoneStatus(m, statusFilter) {
  if (!statusFilter) return true;
  if (statusFilter === "ready") return milestoneDisplayStatus(m).key === "READY";
  return milestoneCategory(m) === statusFilter;
}

export const UNSCOPED_PHASE = "__unscoped__";

export function filterAndSortMilestones(milestones, { search = "", status = "", phase = "", sort = "sequence" }) {
  const term = search.trim().toLowerCase();
  const filtered = milestones.filter((m) => {
    if (!matchesMilestoneStatus(m, status)) return false;
    if (phase === UNSCOPED_PHASE && m.phase_name) return false;
    if (phase && phase !== UNSCOPED_PHASE && m.phase_name !== phase) return false;
    if (!term) return true;
    return [m.name, m.phase_name, m.owner_name, m.module_name].some((v) => String(v || "").toLowerCase().includes(term));
  });
  const progressOf = (m) => (m.task_summary?.progress_percent ?? -1);
  const plannedOf = (m) => m.planned_date || "9999-12-31";
  const sorters = {
    planned_asc: (a, b) => plannedOf(a).localeCompare(plannedOf(b)),
    planned_desc: (a, b) => plannedOf(b).localeCompare(plannedOf(a)),
    progress_desc: (a, b) => progressOf(b) - progressOf(a),
    progress_asc: (a, b) => progressOf(a) - progressOf(b),
  };
  // Array.prototype.sort is stable, so ties keep the API's sequence order.
  return sorters[sort] ? [...filtered].sort(sorters[sort]) : filtered;
}

// ---- Expanded panel: task rows -----------------------------------------

// Fallback for an API that doesn't send progress_bucket yet: same buckets,
// inferred from the resolved status code. The backend's bucket wins
// whenever present so the row filter matches the summary counts exactly.
const COMPLETED_CODES = new Set(["COMPLETED", "COMPLETE", "DONE", "CLOSED", "RESOLVED"]);
const IN_PROGRESS_CODES = new Set(["IN_PROGRESS", "INPROGRESS", "WORKING", "TESTING", "IN_REVIEW", "REVIEW", "WAITING", "ON_HOLD"]);

export function taskBucket(task) {
  if (task.progress_bucket) return task.progress_bucket;
  const code = String(task.status?.code || task.status?.label || "").toUpperCase().replace(/\s+/g, "_");
  if (code === "BLOCKED") return "BLOCKED";
  if (COMPLETED_CODES.has(code)) return "COMPLETED";
  if (IN_PROGRESS_CODES.has(code)) return "IN_PROGRESS";
  return "PENDING";
}

export const TASK_BUCKET_OPTIONS = [
  { value: "COMPLETED", label: "Completed" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "PENDING", label: "Pending" },
  { value: "BLOCKED", label: "Blocked" },
];

export function isTaskOverdue(task, today = dayjs().format("YYYY-MM-DD")) {
  return !!task.due_date && String(task.due_date).slice(0, 10) < today && taskBucket(task) !== "COMPLETED";
}

const PRIORITY_RANK = { CRITICAL: 0, URGENT: 0, HIGH: 1, MEDIUM: 2, NORMAL: 2, LOW: 3 };
const priorityRank = (p) => PRIORITY_RANK[String(p || "").toUpperCase()] ?? 4;

// View controls for the expanded task table. They only narrow/reorder what
// is DISPLAYED -- the panel's stats and progress bar stay the whole
// milestone's figures from the backend. "My Tasks" means assigned to the
// logged-in user (tasks.assigned_to). Newest first orders by task id, which
// the database assigns in creation order.
export function filterMilestoneTasks(tasks, { view = "all", status = "", sort = "newest", search = "" }, currentUserId) {
  const term = search.trim().toLowerCase().replace(/^#/, "");
  const filtered = tasks.filter((t) => {
    if (view === "mine" && !(currentUserId != null && t.assignee && Number(t.assignee.id) === Number(currentUserId))) {
      return false;
    }
    if (status && taskBucket(t) !== status) return false;
    if (!term) return true;
    return String(t.id) === term || String(t.title || "").toLowerCase().includes(term);
  });
  const dueOf = (t) => (t.due_date ? String(t.due_date).slice(0, 10) : "9999-12-31");
  const sorters = {
    oldest: (a, b) => a.id - b.id,
    newest: (a, b) => b.id - a.id,
    due: (a, b) => dueOf(a).localeCompare(dueOf(b)) || a.id - b.id,
    priority: (a, b) => priorityRank(a.priority) - priorityRank(b.priority) || b.id - a.id,
  };
  return [...filtered].sort(sorters[sort] || sorters.newest);
}
